import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ComprovanteRegistrado, imprimirComprovante } from '../components/ComprovanteProtocolo'; // PLENARIUM:COMPROVANTE
import { excluirComConfirmacao } from '../components/ConfirmarExclusao'; // PLENARIUM:LIXEIRA_MATERIAS
import { OpcoesComissoes, BotaoEditarProtocolo, HistoricoTramitacao } from '../components/ProtocoloCorrecoes'; // PLENARIUM:PROTOCOLO_CORRECOES PLENARIUM:PROTOCOLO_COMISSOES
import { Plus, X, FileText, ArrowRightLeft, Search, Paperclip, Clock, Archive, ChevronDown, ChevronUp, Users, Trash2, ToggleLeft, ToggleRight, Printer } from 'lucide-react';

const VAZIO = {
  tipo: 'ENTRADA',
  assunto: '',
  descricao: '',
  remetente: '',
  destinatario: '',
  setor: '',
  arquivoUrl: ''
};

const VAZIO_OPERADOR = { nome: '', cpf: '', email: '', senha: '' };

const SETORES = ['Presidência', 'Mesa Diretora', 'Secretaria', 'Jurídico', 'Financeiro', 'Plenário', 'Protocolo', 'Arquivo'];

const STATUS_LABEL = {
  PENDENTE: { label: 'Pendente', cor: 'badge-amarelo' },
  EM_TRAMITACAO: { label: 'Em Tramitação', cor: 'badge-azul' },
  CONCLUIDO: { label: 'Concluído', cor: 'badge-verde' },
  ARQUIVADO: { label: 'Arquivado', cor: 'badge-cinza' },
};

const TIPO_LABEL = {
  ENTRADA: { label: '📥 Entrada', cor: 'badge-azul' },
  SAIDA: { label: '📤 Saída', cor: 'badge-amarelo' },
};

// PLENARIUM:ANEXO_PROTOCOLO -- o Chrome bloqueia abrir um "data:" direto
// (tela em branco: "Not allowed to navigate top frame to data URL").
// O arquivo vira Blob e abre numa aba nova; se o navegador bloquear, baixa.
function abrirArquivo(url) {
  if (!url) return;
  if (!String(url).startsWith('data:')) { window.open(url, '_blank', 'noopener'); return; }
  try {
    const [cab, corpo] = url.split(',');
    const tipo = (cab.match(/:(.*?);/) || [])[1] || 'application/octet-stream';
    const bin = atob(corpo); const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blobUrl = URL.createObjectURL(new Blob([bytes], { type: tipo }));
    const aba = window.open(blobUrl, '_blank');
    if (!aba) { const a = document.createElement('a'); a.href = blobUrl; a.download = 'anexo-protocolo'; document.body.appendChild(a); a.click(); a.remove(); }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
  } catch (e) { window.alert('Não foi possível abrir o arquivo anexado.'); }
}

export default function Protocolos() {
  const [aba, setAba] = useState('protocolos');
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [modalTramitar, setModalTramitar] = useState(false);
  const [protocoloSelecionado, setProtocoloSelecionado] = useState(null);
  const [expandido, setExpandido] = useState(null);
  const [form, setForm] = useState(VAZIO);
  const [formTramitar, setFormTramitar] = useState({ setor: '', observacao: '', status: 'EM_TRAMITACAO' });
  const [salvando, setSalvando] = useState(false);
  const [comprovante, setComprovante] = useState(null); // PLENARIUM:COMPROVANTE
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  // Operadores
  const [operadores, setOperadores] = useState([]);
  const [loadingOp, setLoadingOp] = useState(false);
  const [modalOperador, setModalOperador] = useState(false);
  const [formOperador, setFormOperador] = useState(VAZIO_OPERADOR);
  const [salvandoOp, setSalvandoOp] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroTipo) params.tipo = filtroTipo;
      if (filtroStatus) params.status = filtroStatus;
      if (busca) params.busca = busca;
      const r = await axios.get('/api/protocolos', { params });
      setLista(r.data);
    } catch { toast.error('Erro ao carregar protocolos'); }
    finally { setLoading(false); }
  };

  const carregarOperadores = async () => {
    setLoadingOp(true);
    try {
      const r = await axios.get('/api/operadores');
      setOperadores(r.data);
    } catch { toast.error('Erro ao carregar operadores'); }
    finally { setLoadingOp(false); }
  };

  useEffect(() => { carregar(); }, [filtroTipo, filtroStatus]);
  useEffect(() => { if (aba === 'operadores') carregarOperadores(); }, [aba]);

  const handleBusca = (e) => {
    e.preventDefault();
    carregar();
  };

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      const rProtocolo = await axios.post('/api/protocolos', form); // PLENARIUM:COMPROVANTE
      toast.success('Protocolo registrado!');
      setComprovante(rProtocolo.data);
      setModal(false);
      setForm(VAZIO);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao registrar protocolo');
    } finally { setSalvando(false); }
  };

  const tramitar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      await axios.post(`/api/protocolos/${protocoloSelecionado.id}/tramitar`, formTramitar);
      toast.success('Protocolo tramitado!');
      setModalTramitar(false);
      setFormTramitar({ setor: '', observacao: '', status: 'EM_TRAMITACAO' });
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao tramitar');
    } finally { setSalvando(false); }
  };

  const arquivar = async (id, numero) => {
    if (!window.confirm(`Arquivar protocolo ${numero}?`)) return;
    try {
      await axios.patch(`/api/protocolos/${id}/status`, { status: 'ARQUIVADO' });
      toast.success('Protocolo arquivado!');
      carregar();
    } catch { toast.error('Erro ao arquivar'); }
  };

  // PLENARIUM:AJUSTES_2110B -- excluir protocolo (com confirmacao)
  const excluirProtocolo = async (p) => {
    if (!window.confirm(`Excluir o protocolo ${p.numero}?\n\nAssunto: ${p.assunto || '—'}\n\nO protocolo e todo o histórico de tramitação saem da lista. Uma cópia fica na lixeira do suporte; se ele já foi movimentado, a exclusão vira um pedido ao suporte.`)) return;
    try {
      if (!(await excluirComConfirmacao(`/api/protocolos/${p.id}`))) return; // PLENARIUM:LIXEIRA_MATERIAS
      toast.success('Protocolo excluído (cópia na lixeira).');
      if (expandido === p.id) setExpandido(null);
      carregar();
    } catch (err) { toast.error(err.response?.data?.error || 'Não foi possível excluir o protocolo.'); }
  };

  const anexarArquivo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo deve ter no máximo 5MB!');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, arquivoUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const salvarOperador = async (e) => {
    e.preventDefault();
    setSalvandoOp(true);
    try {
      await axios.post('/api/operadores', formOperador);
      toast.success('Operador cadastrado!');
      setModalOperador(false);
      setFormOperador(VAZIO_OPERADOR);
      carregarOperadores();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao cadastrar operador');
    } finally { setSalvandoOp(false); }
  };

  const toggleAtivo = async (op) => {
    try {
      await axios.patch(`/api/operadores/${op.id}/ativo`, { ativo: !op.ativo });
      toast.success(op.ativo ? 'Operador desativado!' : 'Operador ativado!');
      carregarOperadores();
    } catch { toast.error('Erro ao atualizar operador'); }
  };

  const excluirOperador = async (id, nome) => {
    if (!window.confirm(`Remover operador "${nome}"?`)) return;
    try {
      await axios.delete(`/api/operadores/${id}`);
      toast.success('Operador removido!');
      carregarOperadores();
    } catch { toast.error('Erro ao remover operador'); }
  };

  const formatCPF = (v) => v.replace(/\D/g, '').slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');

  // Gera um PDF com a lista de protocolos atualmente filtrada em tela
  const gerarPDFProtocolos = () => {
    if (!lista || lista.length === 0) { toast.error('Não há protocolos para gerar o relatório.'); return; }

    let nomeCamara = 'Câmara Municipal';
    try {
      const u = JSON.parse(localStorage.getItem('usuario') || 'null');
      if (u?.camaraNome) nomeCamara = u.camaraNome;
      else if (u?.camara?.nome) nomeCamara = u.camara.nome;
    } catch { /* usa o padrão */ }

    const esc = s => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const emissao = new Date().toLocaleString('pt-BR');

    // Descreve os filtros aplicados, para constar no relatório
    const filtros = [];
    if (filtroTipo) filtros.push('Tipo: ' + (TIPO_LABEL[filtroTipo]?.label || filtroTipo).replace(/[📥📤]/g, '').trim());
    if (filtroStatus) filtros.push('Status: ' + (STATUS_LABEL[filtroStatus]?.label || filtroStatus));
    if (busca) filtros.push('Busca: "' + busca + '"');
    const filtrosTxt = filtros.length ? filtros.join(' | ') : 'Todos os protocolos';

    const linhas = lista.map(p => `
      <tr>
        <td style="font-family:monospace;white-space:nowrap;">${esc(p.numero)}</td>
        <td>${esc((TIPO_LABEL[p.tipo]?.label || p.tipo).replace(/[📥📤]/g, '').trim())}</td>
        <td>${esc(p.assunto)}</td>
        <td>${esc(p.remetente)} → ${esc(p.destinatario)}</td>
        <td>${esc(p.setor)}</td>
        <td>${esc(STATUS_LABEL[p.status]?.label || p.status)}</td>
        <td style="white-space:nowrap;">${p.criadoEm ? new Date(p.criadoEm).toLocaleDateString('pt-BR') : ''}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Relatório de Protocolos</title>
    <style>
      body{font-family:Arial,Helvetica,sans-serif;padding:32px;color:#111;font-size:12px;}
      h1{font-size:16px;text-align:center;margin:4px 0;}
      .sub{text-align:center;color:#555;font-size:11px;margin:2px 0;}
      .meta{margin:14px 0 8px;font-size:11px;color:#333;}
      table{width:100%;border-collapse:collapse;margin-top:8px;}
      th,td{border:1px solid #ccc;padding:5px 7px;text-align:left;vertical-align:top;}
      th{background:#1a2d5a;color:#fff;font-size:11px;}
      tr:nth-child(even){background:#f4f6fa;}
      .rodape{margin-top:16px;font-size:10px;color:#777;text-align:right;}
      @media print{body{padding:12px;}}
    </style></head><body>
      <h1>${esc(nomeCamara.toUpperCase())}</h1>
      <p class="sub">Relatório de Protocolos — Protocolo Digital</p>
      <div class="meta"><strong>Filtro:</strong> ${esc(filtrosTxt)} &nbsp;•&nbsp; <strong>Total:</strong> ${lista.length} protocolo(s) &nbsp;•&nbsp; <strong>Emitido em:</strong> ${esc(emissao)}</div>
      <table>
        <thead>
          <tr><th>Número</th><th>Tipo</th><th>Assunto</th><th>Remetente → Destinatário</th><th>Setor</th><th>Status</th><th>Data</th></tr>
        </thead>
        <tbody>${linhas}</tbody>
      </table>
      <p class="rodape">Documento gerado pelo Plenarium Digital em ${esc(emissao)}.</p>
    </body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  return (
    <div>
      <ComprovanteRegistrado protocolo={comprovante} onFechar={() => setComprovante(null)} /> {/* PLENARIUM:COMPROVANTE */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Protocolo Digital</h1>
          <p className="page-subtitle">{lista.length} protocolo(s) encontrado(s)</p>
        </div>
        {aba === 'protocolos' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={gerarPDFProtocolos}>
              <Printer size={16} /> Relatório PDF
            </button>
            <button className="btn btn-primary" onClick={() => { setForm(VAZIO); setModal(true); }}>
              <Plus size={16} /> Novo Protocolo
            </button>
          </div>
        )}
        {aba === 'operadores' && (
          <button className="btn btn-primary" onClick={() => { setFormOperador(VAZIO_OPERADOR); setModalOperador(true); }}>
            <Plus size={16} /> Novo Operador
          </button>
        )}
      </div>

      {/* Abas */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid var(--cinza)' }}>
        <button
          onClick={() => setAba('protocolos')}
          style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', borderBottom: aba === 'protocolos' ? '2px solid var(--primaria)' : '2px solid transparent', color: aba === 'protocolos' ? 'var(--primaria)' : 'var(--texto-suave)', marginBottom: -2 }}
        >
          <FileText size={15} style={{ marginRight: 6 }} />Protocolos
        </button>
        <button
          onClick={() => setAba('operadores')}
          style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', borderBottom: aba === 'operadores' ? '2px solid var(--primaria)' : '2px solid transparent', color: aba === 'operadores' ? 'var(--primaria)' : 'var(--texto-suave)', marginBottom: -2 }}
        >
          <Users size={15} style={{ marginRight: 6 }} />Operadores
        </button>
      </div>

      {/* Aba Protocolos */}
      {aba === 'protocolos' && (
        <>
          <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <form onSubmit={handleBusca} style={{ display: 'flex', gap: 8, flex: 1, minWidth: 200 }}>
                <input className="form-input" placeholder="Buscar por número, assunto ou remetente..." value={busca} onChange={e => setBusca(e.target.value)} style={{ flex: 1 }} />
                <button className="btn btn-secondary" type="submit"><Search size={15} /></button>
              </form>
              <select className="form-input form-select" style={{ width: 140 }} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
                <option value="">Todos os tipos</option>
                <option value="ENTRADA">📥 Entrada</option>
                <option value="SAIDA">📤 Saída</option>
              </select>
              <select className="form-input form-select" style={{ width: 160 }} value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
                <option value="">Todos os status</option>
                <option value="PENDENTE">Pendente</option>
                <option value="EM_TRAMITACAO">Em Tramitação</option>
                <option value="CONCLUIDO">Concluído</option>
                <option value="ARQUIVADO">Arquivado</option>
              </select>
            </div>
          </div>

          <div className="card">
            {loading ? (
              <div className="loading-screen" style={{ minHeight: 200 }}><div className="spinner" /></div>
            ) : lista.length === 0 ? (
              <div className="sem-dados"><p>Nenhum protocolo encontrado.</p></div>
            ) : lista.map(p => (
              <div key={p.id} style={{ borderBottom: '1px solid var(--cinza)', padding: '14px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <strong style={{ fontFamily: 'monospace', fontSize: '0.95rem' }}>{p.numero}</strong>
                      <span className={`badge ${TIPO_LABEL[p.tipo]?.cor}`}>{TIPO_LABEL[p.tipo]?.label}</span>
                      <span className={`badge ${STATUS_LABEL[p.status]?.cor}`}>{STATUS_LABEL[p.status]?.label}</span>
                      {p.arquivoUrl && <Paperclip size={13} style={{ color: 'var(--texto-suave)' }} />}
                    </div>
                    <p style={{ fontWeight: 600, marginBottom: 2 }}>{p.assunto}</p>
                    <p style={{ fontSize: '0.8rem', color: 'var(--texto-suave)' }}>
                      De: <strong>{p.remetente}</strong> → Para: <strong>{p.destinatario}</strong> | Setor: {p.setor}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--texto-suave)', marginTop: 2 }}>
                      <Clock size={11} style={{ marginRight: 3 }} />
                      {new Date(p.criadoEm).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {p.arquivoUrl && (
                      <button type="button" onClick={() => abrirArquivo(p.arquivoUrl)} className="btn btn-secondary btn-sm">{/* PLENARIUM:ANEXO_PROTOCOLO */}
                        <Paperclip size={13} /> Ver arquivo
                      </button>
                    )}
                    {p.status !== 'ARQUIVADO' && (
                      <>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setProtocoloSelecionado(p); setModalTramitar(true); }}>
                          <ArrowRightLeft size={13} /> Tramitar
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => arquivar(p.id, p.numero)}>
                          <Archive size={13} />
                        </button>
                      </>
                    )}
                    <BotaoEditarProtocolo protocolo={p} onSalvo={carregar} /> {/* PLENARIUM:PROTOCOLO_CORRECOES */}
                    <button className="btn btn-secondary btn-sm" onClick={() => imprimirComprovante(p)} title="Imprimir comprovante"> {/* PLENARIUM:COMPROVANTE */}
                      <Printer size={13} /> Comprovante
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => excluirProtocolo(p)} title="Excluir protocolo"> {/* PLENARIUM:AJUSTES_2110B */}
                      <Trash2 size={13} />
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setExpandido(expandido === p.id ? null : p.id)}>
                      {expandido === p.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                  </div>
                </div>
                {expandido === p.id && <HistoricoTramitacao protocolo={p} onMudou={carregar} />} {/* PLENARIUM:PROTOCOLO_CORRECOES */}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Aba Operadores */}
      {aba === 'operadores' && (
        <div className="card">
          {loadingOp ? (
            <div className="loading-screen" style={{ minHeight: 200 }}><div className="spinner" /></div>
          ) : operadores.length === 0 ? (
            <div className="sem-dados">
              <p>Nenhum operador cadastrado.</p>
              <p style={{ fontSize: '0.85rem', color: 'var(--texto-suave)', marginTop: 8 }}>
                Operadores têm acesso exclusivo ao módulo de protocolo.<br />
                Login em: <strong>/login/operador</strong>
              </p>
            </div>
          ) : operadores.map(op => (
            <div key={op.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--cinza)' }}>
              <div>
                <p style={{ fontWeight: 600 }}>{op.nome}</p>
                <p style={{ fontSize: '0.8rem', color: 'var(--texto-suave)' }}>CPF: {op.cpf}{op.email ? ` • ${op.email}` : ''}</p>
                <p style={{ fontSize: '0.75rem', color: 'var(--texto-suave)' }}>
                  Cadastrado em {new Date(op.criadoEm).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className={`badge ${op.ativo ? 'badge-verde' : 'badge-cinza'}`}>
                  {op.ativo ? 'Ativo' : 'Inativo'}
                </span>
                <button className="btn btn-secondary btn-sm" onClick={() => toggleAtivo(op)} title={op.ativo ? 'Desativar' : 'Ativar'}>
                  {op.ativo ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => excluirOperador(op.id, op.nome)}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Novo Protocolo */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2 className="modal-title"><FileText size={18} /> Novo Protocolo</h2>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={salvar}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Tipo *</label>
                  <select className="form-input form-select" value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} required>
                    <option value="ENTRADA">📥 Entrada</option>
                    <option value="SAIDA">📤 Saída</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Setor Atual *</label>
                  <select className="form-input form-select" value={form.setor} onChange={e => setForm({ ...form, setor: e.target.value })} required>
                    <option value="">Selecione</option>
                    {SETORES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Assunto *</label>
                  <input className="form-input" value={form.assunto} onChange={e => setForm({ ...form, assunto: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Remetente *</label>
                  <input className="form-input" value={form.remetente} onChange={e => setForm({ ...form, remetente: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Destinatário *</label>
                  <input className="form-input" value={form.destinatario} onChange={e => setForm({ ...form, destinatario: e.target.value })} required />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Descrição</label>
                  <textarea className="form-input" rows={3} value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} style={{ resize: 'vertical' }} />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Anexar PDF (máx. 5MB)</label>
                  <input type="file" accept=".pdf,image/*" className="form-input" onChange={anexarArquivo} />
                  {form.arquivoUrl && <p style={{ fontSize: '0.78rem', color: 'var(--verde)', marginTop: 4 }}>✅ Arquivo anexado</p>}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={salvando}>{salvando ? 'Salvando...' : 'Registrar Protocolo'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Tramitar */}
      {modalTramitar && protocoloSelecionado && (
        <div className="modal-overlay" onClick={() => setModalTramitar(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title"><ArrowRightLeft size={18} /> Tramitar — {protocoloSelecionado.numero}</h2>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setModalTramitar(false)}><X size={20} /></button>
            </div>
            <form onSubmit={tramitar}>
              <div className="form-group">
                <label className="form-label">Encaminhar para o Setor *</label>
                <select className="form-input form-select" value={formTramitar.setor} onChange={e => setFormTramitar({ ...formTramitar, setor: e.target.value })} required>
                  <option value="">Selecione</option>
                  {SETORES.map(s => <option key={s} value={s}>{s}</option>)}
                  <OpcoesComissoes /> {/* PLENARIUM:PROTOCOLO_COMISSOES */}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status *</label>
                <select className="form-input form-select" value={formTramitar.status} onChange={e => setFormTramitar({ ...formTramitar, status: e.target.value })}>
                  <option value="EM_TRAMITACAO">Em Tramitação</option>
                  <option value="CONCLUIDO">Concluído</option>
                  <option value="ARQUIVADO">Arquivado</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Observação</label>
                <textarea className="form-input" rows={3} value={formTramitar.observacao} onChange={e => setFormTramitar({ ...formTramitar, observacao: e.target.value })} style={{ resize: 'vertical' }} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalTramitar(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={salvando}>{salvando ? 'Salvando...' : 'Tramitar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Novo Operador */}
      {modalOperador && (
        <div className="modal-overlay" onClick={() => setModalOperador(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title"><Users size={18} /> Novo Operador de Protocolo</h2>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setModalOperador(false)}><X size={20} /></button>
            </div>
            <form onSubmit={salvarOperador}>
              <div className="form-group">
                <label className="form-label">Nome *</label>
                <input className="form-input" value={formOperador.nome} onChange={e => setFormOperador({ ...formOperador, nome: e.target.value })} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">CPF *</label>
                  <input className="form-input" value={formOperador.cpf} onChange={e => setFormOperador({ ...formOperador, cpf: formatCPF(e.target.value) })} placeholder="000.000.000-00" required />
                </div>
                <div className="form-group">
                  <label className="form-label">E-mail</label>
                  <input className="form-input" type="email" value={formOperador.email} onChange={e => setFormOperador({ ...formOperador, email: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Senha *</label>
                <input className="form-input" type="password" value={formOperador.senha} onChange={e => setFormOperador({ ...formOperador, senha: e.target.value })} required />
              </div>
              <p style={{ fontSize: '0.78rem', color: 'var(--texto-suave)', marginBottom: 12 }}>
                🔑 Login do operador: <strong>http://192.168.15.57:3000/login/operador</strong>
              </p>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalOperador(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={salvandoOp}>{salvandoOp ? 'Salvando...' : 'Cadastrar Operador'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}